import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, formatInt, formatMoney, formatPercent } from "@/lib/api";
import { MetricLabel, CpaValue } from "@/lib/metrics";
import DailyBriefSection from "@/components/DailyBriefSection";
import HomeFriendPathsSection from "@/components/HomeFriendPathsSection";
import HomeRoasSection from "@/components/HomeRoasSection";
import FriendStatsStrip from "@/components/FriendStatsStrip";
import { AccuracyBanner } from "@/components/DataAccuracy";
import {
  DollarSign, Eye, MessageCircle, Flame, Target, GitCompareArrows, Building2, ArrowRight, KeyRound, Lightbulb, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { SyncSpinner } from "@/components/SyncSpinner";

const BRAND_ORANGE = "#F26A1F";

const SORT_OPTIONS = [
  { value: "revenue",         label: "營收",       isRevenue: true,  format: "money" },
  { value: "spend",           label: "花費",       format: "money" },
  { value: "impressions",     label: "曝光",       format: "int" },
  { value: "clicks",          label: "點擊（全部）", format: "int" },
  { value: "link_clicks",     label: "連結點擊",   format: "int" },
  { value: "ctr",             label: "CTR",       format: "pct" },
  { value: "cpa",             label: "CPA",       format: "money", asc: true },
  { value: "line_hot",        label: "熱單",       format: "int" },
  { value: "line_inquiry",    label: "問房",       format: "int" },
  { value: "line_real_human", label: "真人新訊息", format: "int" },
  { value: "friend_adds",     label: "加好友",     format: "int" },
  { value: "friend_net",      label: "淨增好友",   format: "int" },
];

const STORAGE_KEY = "home_top5_sort";

function KpiCard({ title, value, hintKey, icon: Icon, variant = "orange", testid }) {
  const variants = {
    orange: "bg-gradient-to-br from-[#FFF7ED] to-[#FEF3E8] border-[#FEE4CC] text-[#8A3A0C]",
    blue:   "bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] border-[#BFDBFE] text-[#1E40AF]",
    amber:  "bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7] border-[#FDE68A] text-[#92400E]",
  };
  const iconBg = {
    orange: "bg-[#F26A1F] text-white",
    blue: "bg-[#2E7FBF] text-white",
    amber: "bg-[#F59E0B] text-white",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} data-testid={testid}>
      <Card className={`rounded-2xl shadow-sm hover:shadow-md transition-shadow ${variants[variant]}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <MetricLabel label={title} hintKey={hintKey} className="text-xs font-medium uppercase tracking-wider opacity-80" />
            <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${iconBg[variant]}`}>
              <Icon className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight tabular-nums">{value}</div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

const PAGES = [
  { to: "/meta-poc", label: "Meta 廣告成效", desc: "逐檔活動的花費與轉換成效", Icon: Target, variant: "orange" },
  { to: "/line-insights", label: "LINE 洞察", desc: "真人訊息、問房、熱單", Icon: MessageCircle, variant: "blue" },
  { to: "/compare", label: "跨域對比", desc: "多場域並列、跨時段、月份趨勢", Icon: GitCompareArrows, variant: "orange" },
  { to: "/settings/properties", label: "場域管理", desc: "別名、排序、LINE 整合", Icon: Building2, variant: "blue" },
];

function fmtMetric(opt, v) {
  if (v == null) return "—";
  if (opt.format === "money") return formatMoney(v, "TWD");
  if (opt.format === "pct") return formatPercent(v);
  return formatInt(v);
}

export default function HomePage() {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [counters, setCounters] = useState(null);
  const [daily, setDaily] = useState([]);
  const [tokenConfigured, setTokenConfigured] = useState(null); // null = not yet resolved
  const [compareRows, setCompareRows] = useState([]);
  // UX preference only (Top-5 sort metric) — not sensitive data.
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(STORAGE_KEY) || "revenue");

  // UX preference only (not sensitive data).
  useEffect(() => { localStorage.setItem(STORAGE_KEY, sortBy); }, [sortBy]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cRes, dRes, tRes, mpRes] = await Promise.all([
          api.get("/line/insights/counters", { params: { date_preset: "last_30d" } }),
          api.get("/line/insights/daily-series", { params: { date_preset: "last_30d" } }),
          api.get("/settings/meta-token").catch(() => ({ data: { configured: false } })),
          api.get("/compare/multi-property"),
        ]);
        setCounters(cRes.data);
        setDaily(dRes.data.series || []);
        setTokenConfigured(!!tRes.data?.configured);
        setCompareRows(mpRes.data?.properties || []);
      } catch (e) {
        toast.error(errorMessage(e, "載入總覽資料失敗"));
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    })();
  }, []);

  const totalSpend = compareRows.reduce((s, p) => s + (p.spend || 0), 0);
  const totalImpressions = compareRows.reduce((s, p) => s + (p.impressions || 0), 0);
  const totalRealHuman = counters?.current?.real_human || 0;
  const totalHot = counters?.current?.hot || 0;

  const activeOption = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
  const fallbackActive = activeOption.isRevenue; // revenue → fallback to spend
  const sortKey = fallbackActive ? "spend" : sortBy;
  const displayOption = fallbackActive ? SORT_OPTIONS.find(o => o.value === "spend") : activeOption;

  const sortedTop5 = useMemo(() => {
    const copy = compareRows.filter(r => (r[sortKey] ?? null) !== null);
    const asc = !!activeOption.asc && !fallbackActive;
    copy.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return asc ? av - bv : bv - av;
    });
    return copy.slice(0, 5);
  }, [compareRows, sortKey, activeOption.asc, fallbackActive]);

  const maxVal = Math.max(1, ...sortedTop5.map(r => Math.abs(Number(r[sortKey] || 0))));
  const chartData = (daily || []).map((d) => ({ date: (d.date || "").slice(5), hot: d.hot || 0 }));

  if (loaded && tokenConfigured === false) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-5" data-testid="home-empty-state">
        <div className="h-16 w-16 rounded-2xl bg-[#FEF3E8] mx-auto flex items-center justify-center">
          <KeyRound className="h-8 w-8 text-[#F26A1F]" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">歡迎使用 Lakeze 人客多</h2>
        <p className="text-sm text-slate-500">先完成 Meta Token 設定，就能看到完整的廣告 + LINE 數據。</p>
        <Link to="/settings">
          <Button className="bg-[#F26A1F] hover:bg-[#D55B15] text-white rounded-full px-6" data-testid="home-cta-settings">
            前往 Meta 設定貼上 Token <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="home-page">
      <AccuracyBanner testid="home-accuracy-banner" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {!loaded ? (
          <>{[0,1,2,3].map(i => (
            <Card key={i} className="rounded-2xl bg-white border-slate-200" data-testid={`home-kpi-skeleton-${i}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-24 bg-slate-100" />
                  <Skeleton className="h-9 w-9 rounded-xl bg-slate-100" />
                </div>
                <Skeleton className="h-10 w-24 bg-slate-100" />
              </CardContent>
            </Card>
          ))}</>
        ) : (
          <>
            <KpiCard testid="home-kpi-spend" title="總廣告花費 (近30天)" hintKey="spend" value={formatMoney(totalSpend, "TWD")} icon={DollarSign} variant="orange" />
            <KpiCard testid="home-kpi-impressions" title="總曝光 (近30天)" hintKey="impressions" value={formatInt(totalImpressions)} icon={Eye} variant="blue" />
            <KpiCard testid="home-kpi-real-human" title="總真人訊息 (近30天)" hintKey="real_human" value={formatInt(totalRealHuman)} icon={MessageCircle} variant="blue" />
            <KpiCard testid="home-kpi-hot" title="總熱單 (近30天)" hintKey="hot" value={formatInt(totalHot)} icon={Flame} variant="amber" />
          </>
        )}
      </div>

      {/* F4 — 加好友 / 封鎖 / 淨增 / 封鎖率 (近 30 天) */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="home-friend-block">
        <FriendStatsStrip rangeKey="30d" showByProperty withEffective testid="home-friend-stats" />
      </div>

      {/* R1 — ROAS KPI with traffic lights */}
      <HomeRoasSection />

      {/* AD1 — Daily AI brief (collapsed details inside) */}
      <DailyBriefSection />

      {/* AH3 — Friend-path attribution home section */}
      <HomeFriendPathsSection />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="rounded-2xl bg-white border-slate-200 shadow-sm" data-testid="home-top-properties">
          <CardHeader className="flex-row items-start justify-between space-y-0 gap-3">
            <div>
              <CardTitle className="text-slate-900 text-base">Top 5 場域</CardTitle>
              <CardDescription className="text-slate-500">依{displayOption.label}排序 · 近 30 天</CardDescription>
              {fallbackActive && (
                <div className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="home-top-revenue-notice">
                  <Lightbulb className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>營收尚未連接，暫以花費排序 → <Link to="/settings" className="underline hover:text-amber-900">設定營收 API</Link></span>
                </div>
              )}
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[128px] h-8 bg-white border-slate-300 text-slate-700 text-xs" data-testid="home-top-sort-select">
                <span className="text-slate-500 mr-1">排序：</span><SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-slate-800 focus:bg-slate-100 focus:text-slate-900">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-3">
            {!loaded && (
              <>
                {[0,1,2,3,4].map(i => (
                  <div key={i} className="flex items-center gap-3" data-testid={`home-top-skeleton-${i}`}>
                    <Skeleton className="w-28 h-4 bg-slate-100" />
                    <Skeleton className="flex-1 h-6 bg-slate-100 rounded-full" />
                    <Skeleton className="w-24 h-4 bg-slate-100" />
                  </div>
                ))}
              </>
            )}
            {loaded && sortedTop5.length === 0 && <div className="text-center py-8 text-sm text-slate-500">無資料</div>}
            {loaded && sortedTop5.map((p) => {
              const v = Number(p[sortKey] || 0);
              const w = (Math.abs(v) / maxVal) * 100;
              return (
                <div key={p.id} className="flex items-center gap-3" data-testid={`home-top-row-${p.id}`}>
                  <div className="w-28 text-sm text-slate-700 truncate">{p.name}</div>
                  <div className="relative flex-1 h-6 rounded-full bg-slate-100 overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 rounded-full bg-gradient-to-r from-[#F26A1F] to-[#F39A4B]" style={{ width: `${Math.max(2, w)}%` }} />
                  </div>
                  <div className="w-32 text-right text-sm font-semibold tabular-nums text-slate-900">
                    {sortKey === "cpa" ? <CpaValue value={p[sortKey]} testid={`home-top-cpa-${p.id}`}>{fmtMetric(displayOption, p[sortKey])}</CpaValue> : fmtMetric(displayOption, p[sortKey])}
                    {fallbackActive && <div className="text-[10px] font-normal text-slate-400">尚無營收</div>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-white border-slate-200 shadow-sm" data-testid="home-hot-trend">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base">熱單趨勢（近 30 天）</CardTitle>
            <CardDescription className="text-slate-500">每日 LEVEL_3 熱單數</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              {!loaded ? (
                <Skeleton className="h-full w-full bg-slate-100 rounded-lg" data-testid="home-trend-skeleton" />
              ) : chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">尚無熱單資料</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" allowDecimals={false} />
                    <RTooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, color: "#0f172a" }} />
                    <Line type="monotone" dataKey="hot" stroke={BRAND_ORANGE} strokeWidth={2} dot={{ r: 3, fill: BRAND_ORANGE }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
        {PAGES.map((p) => (
          <Link key={p.to} to={p.to} data-testid={`home-nav-card-${p.to.replace(/\//g, "-")}`}>
            <Card className={`rounded-2xl border-slate-200 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${p.variant === "orange" ? "bg-[#FEF3E8] text-[#F26A1F]" : "bg-[#DBEAFE] text-[#2E7FBF]"}`}>
                  <p.Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{p.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{p.desc}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
